import React from 'react';
import styled from '@emotion/styled';
import { isNull } from 'util';
import differenceInYears from 'date-fns/differenceInYears';
import { debounce } from 'lodash-es';

import { isChildAgeValid } from '../../../components/RequestForm/helpers';
import { AdultsIcon } from '../../../components/Icons/AdultsIcon';
import { KidsIcon } from '../../../components/Icons/KidsIcon';
import { IRequestFormReducer } from '../../../interfaces/IRequestForm';
import { ICruiseReducer } from '../../../interfaces';
import { IRouterReducer } from '../../../reducers/router';
import { upperFirstLetter } from '../../../helpers/string';
import { __ } from '../../../helpers/TranslationService';
import { DatePickerSelect } from '../../RequestForm/components/DatePickerSelect';
import { getChildAgeErrorMessage } from '../../../components/RequestForm/helpers';

const PassengerSelectionContainer = styled.div`
    padding-top: 48px;
    margin-bottom: 36px;
`;

const SectionHeading = styled.p`
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 16px;
`;

const PassengerRow = styled.div`
    display: flex;
    font-size: 14px;
    align-items: center;
    margin-bottom: 24px;

    svg {
        fill: ${(props) => props.theme.icon.brandColor1};
        width: 16px;
        height: 16px;
    }
`;

const PassengerDescription = styled.div`
    display: flex;
    align-items: center;
`;

const PassengerType = styled.span`
    font-weight: 700;
    margin: 0 8px;
`;

const PassengerActions = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
`;

const PassengerCount = styled.span`
    font-weight: 700;
    font-size: 16px;
    margin: 0 8px;
`;

const PassengerActionButton = styled.button`
    border: 2px solid ${(props) => props.theme.colors.seashell[100]};
    border-radius: 50%;
    box-shadow: 0px 6px 8px 0px rgba(0, 0, 0, 0.02);
    color: ${(props) => props.theme.icon.brandColor1};
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 36px;
    padding: 0;
    background: #fff;
    cursor: pointer;
    font-size: 20px;
    font-weight: bold;

    &:disabled {
        opacity: 0.5;
    }
`;

const ChildAgeTitle = styled.p`
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 8px;
`;

const ChildAgesContainer = styled.div`
    border-bottom: 2px solid ${(props) => props.theme.colors.seashell[100]};
    padding-bottom: 24px;
`;

const datePickerStyles: Record<string, React.CSSProperties> = {
    wrapper: {
        flex: '0 0 50%',
        marginLeft: 0,
        marginRight: 0,
        marginBottom: '8px',
        padding: 0,
        boxShadow: '0px 6px 8px 0px rgba(0, 0, 0, 0.02)',
    },
    selectWrapper: {
        border: '2px solid rgba(230, 231, 233, 1)',
        background: '#fff',
        borderRadius: '4px',
    },
};

interface IPassengerSelectSection {
    isSubmitted?: boolean;
    requestForm: IRequestFormReducer;
    cruisePage: ICruiseReducer;
    router: IRouterReducer;
    setAdultsNum(num: number): void;
    setChildrenNum(num: number): void;
    setChildAge(age: number, birthday: Date, index: number, isValid: boolean): Function;
}

const MIN_ADULT_AGE_FALLBACK = 18;
const MAX_PASSENGERS = 9;
export const PASSENGER_SELECTION_ID = 'passenger-selection-container';

export const PassengerSelectSection = (props: IPassengerSelectSection) => {
    const { adultsNum, childrenNum, childAges, selectedPax } = props.requestForm;

    const [localAdultNum, setLocalAdultNum] = React.useState(0);
    const [localChildNum, setLocalChildNum] = React.useState(0);

    const dateOfDeparture = props.cruisePage.sails[props.cruisePage.selectedSailIndex]?.departure;
    const { nights, company } = props.cruisePage;

    const adultAgeStart = selectedPax.adult.minAge || MIN_ADULT_AGE_FALLBACK;
    const maxChildAge = adultAgeStart - 1;

    const updatePassengersStoreNumbers = (passenger: 'adult' | 'child', num: number) => {
        const setPassengerNum = {
            adult: props.setAdultsNum,
            child: props.setChildrenNum,
        }[passenger];

        setPassengerNum(num);
    };

    const debouncedAdultsStoreUpdate = React.useCallback(
        debounce((num: number) => updatePassengersStoreNumbers('adult', num), 300),
        [],
    );

    const debouncedChildsStoreUpdate = React.useCallback(
        debounce((num: number) => updatePassengersStoreNumbers('child', num), 300),
        [],
    );

    const handleNumChange = (passenger: 'adult' | 'child', type: 'add' | 'subtract') => {
        const isChild = passenger === 'child';
        const isAdult = passenger === 'adult';

        const updateLocalPassengerNum = {
            adult: setLocalAdultNum,
            child: setLocalChildNum,
        }[passenger];

        const debouncedStoreUpdate = {
            adult: debouncedAdultsStoreUpdate,
            child: debouncedChildsStoreUpdate,
        }[passenger];

        const currentNumber = isChild ? localChildNum : localAdultNum;
        let nextNumber = currentNumber;

        if (type === 'subtract') {
            if (isChild && currentNumber > 0) {
                nextNumber = currentNumber - 1;
            }
            if (isAdult && currentNumber > 1) {
                nextNumber = currentNumber - 1;
            }
        }

        if (type === 'add' && currentNumber < MAX_PASSENGERS) {
            nextNumber = currentNumber + 1;
        }

        updateLocalPassengerNum(nextNumber);
        debouncedStoreUpdate(nextNumber);
    };

    const handleChildAgeChange = (index, dateOfDeparture) => (date: Date) => {
        const { company, nights } = props.cruisePage;

        if (isNull(date) || isNaN(date.getTime())) {
            props.setChildAge(null, null, index, false);
            return;
        }

        props.setChildAge(
            differenceInYears(dateOfDeparture * 1000, date),
            date,
            index,
            isChildAgeValid(dateOfDeparture, date, nights, company.bookingServiceCode),
        );
    };

    React.useEffect(() => {
        if (adultsNum !== localAdultNum) {
            setLocalAdultNum(props.requestForm.adultsNum);
        }
        if (childrenNum !== localChildNum) {
            setLocalChildNum(props.requestForm.childrenNum);
        }
    }, [childrenNum, adultsNum]);

    const canRemoveChild = localChildNum > 0;
    const canRemoveAdult = localAdultNum > 1;

    return (
        <PassengerSelectionContainer id={PASSENGER_SELECTION_ID}>
            <SectionHeading> {__('passengers', 'dreamlines')} </SectionHeading>
            <div>
                <PassengerRow>
                    <PassengerDescription>
                        <AdultsIcon />
                        <PassengerType>
                            {upperFirstLetter(__('general.adults', 'dreamlines'))}
                        </PassengerType>
                        <span>
                            <span>{`${adultAgeStart}+ ${__(
                                `cruiseData.pax.years`,
                                'dreamlines',
                            )}`}</span>
                        </span>
                    </PassengerDescription>

                    <PassengerActions>
                        <PassengerActionButton
                            disabled={!canRemoveAdult}
                            onClick={() => handleNumChange('adult', 'subtract')}
                        >
                            —
                        </PassengerActionButton>
                        <PassengerCount> {localAdultNum} </PassengerCount>
                        <PassengerActionButton
                            disabled={localAdultNum === MAX_PASSENGERS}
                            onClick={() => handleNumChange('adult', 'add')}
                        >
                            +
                        </PassengerActionButton>
                    </PassengerActions>
                </PassengerRow>

                <PassengerRow>
                    <PassengerDescription>
                        <KidsIcon />
                        <PassengerType>
                            {upperFirstLetter(__('general.children', 'dreamlines'))}
                        </PassengerType>
                        <span>
                            <span>{`0-${maxChildAge} ${__(
                                `cruiseData.pax.years`,
                                'dreamlines',
                            )}`}</span>
                        </span>
                    </PassengerDescription>

                    <PassengerActions>
                        <PassengerActionButton
                            disabled={!canRemoveChild}
                            onClick={() => handleNumChange('child', 'subtract')}
                        >
                            —
                        </PassengerActionButton>
                        <PassengerCount> {localChildNum} </PassengerCount>
                        <PassengerActionButton
                            disabled={localChildNum === MAX_PASSENGERS}
                            onClick={() => handleNumChange('child', 'add')}
                        >
                            +
                        </PassengerActionButton>
                    </PassengerActions>
                </PassengerRow>
            </div>

            {childAges.length > 0 && (
                <ChildAgesContainer>
                    <ChildAgeTitle>
                        {__('personalData.childrenAge.ageByDeparture', 'dreamlines')}:
                    </ChildAgeTitle>

                    {childAges.map((child, index) => {
                        const errorMessage = getChildAgeErrorMessage(
                            child,
                            company.bookingServiceCode,
                            nights,
                            props.isSubmitted,
                        );
                        return (
                            <DatePickerSelect
                                key={index}
                                wrapperStyle={datePickerStyles.wrapper}
                                selectWrapperStyle={datePickerStyles.selectWrapper}
                                isMax={true}
                                isError={props.isSubmitted && !!errorMessage}
                                errorMessage={errorMessage}
                                isFirstInvalidFormField={false}
                                testSelectorName="childAge"
                                placeholder={__('YYYY-MM-DD', 'dreamlines')}
                                onChange={handleChildAgeChange(index, dateOfDeparture)}
                                value={child.birthday}
                                dateOfDeparture={dateOfDeparture}
                            />
                        );
                    })}
                </ChildAgesContainer>
            )}
        </PassengerSelectionContainer>
    );
};
